// src/mechanism/nativeBridge.js
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

import { PushNotifications } from '@capacitor/push-notifications';

export const isHarmony = () => typeof window !== 'undefined' && typeof window.HarmonyJSBridge !== 'undefined';
export const isIOS = () => Capacitor.getPlatform() === 'ios' && !isHarmony();
export const isAndroid = () => Capacitor.getPlatform() === 'android' && !isHarmony();
export const isNative = () => Capacitor.isNativePlatform() || isHarmony();

export const isWxMiniProgram = () => {
    const ua = navigator.userAgent.toLowerCase();
    // 微信官方推荐的判断方式 (包括原生小程序及企业微信小程序)
    return ua.includes('miniprogram') || window.__wxjs_environment === 'miniprogram';
};


export const NativeBridge = {
    // 1. 获取当前 App 原生版本号 (核心！)
    async getAppVersion() {
        if (isHarmony()) {
            const res = await window.HarmonyJSBridge.getAppVersion();
            return JSON.parse(res).version;
        } else if (Capacitor.isNativePlatform()) {
            const info = await CapacitorApp.getInfo();
            return info.version; // 返回如 "1.0.0"
        }
        return "1.0.0"; // Web 兜底
    },

    // 2. 唤起应用商店
    async openAppStore(appId) {
        if (!appId) return;

        let schemaUrl = '';

        if (isHarmony()) {
            schemaUrl = `store://appgallery.huawei.com/app/detail?id=${appId}`;
            await window.HarmonyJSBridge.openAppStore(schemaUrl);
        } else if (isAndroid()) {
            // Android 统一 Schema 意图
            schemaUrl = `market://details?id=${appId}`;
            // 依赖我们手写的 Capacitor 插件去执行 Intent
            await Capacitor.nativePromise('AppStore', 'openAppStore', { url: schemaUrl });
        } else if (isIOS()) {
            // iOS App Store 统一 Schema
            schemaUrl = `itms-apps://itunes.apple.com/app/id${appId}`;
            await Capacitor.nativePromise('SuperLink', 'openAppStore', { url: schemaUrl });
        }
    },

    async initPushSDK() {
        if (isHarmony()) {
            // 鸿蒙按需处理
        } else if (isAndroid()) {
            try {
                return await Capacitor.nativePromise('PushBridge', 'initPushSDK', {});
            } catch (e) {
                console.error("Android 推送 SDK 初始化失败", e);
            }
        } else if (isIOS()) {
            // iOS 通常不需要在启动时显式 init SDK，保留空逻辑即可
        }
    },


    initPushListeners() {
        if (isIOS()) {
            PushNotifications.removeAllListeners().then(() => {

                // 1. 监听通知被点击 (后台/冷启动)
                PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                    console.log('用户点击了推送通知: ', notification);
                    const data = notification.notification.data;
                    if (window.__ON_PUSH_CLICKED__) {
                        window.__ON_PUSH_CLICKED__(data);
                    }
                });

                // 🌟 修复点 3：前台收到通知时，弃用系统横幅，抛给 Vue 渲染精美弹窗
                PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    console.log('前台收到推送通知: ', notification);
                    const title = notification.title || '新提醒';
                    const body = notification.body || '';
                    const data = notification.data || {};

                    // 不再使用 Vant 的 showNotify，而是调用前端注册的前台接收器
                    if (window.__ON_PUSH_RECEIVED__) {
                        window.__ON_PUSH_RECEIVED__({ title, body, data });
                    }
                });
            });
        }
    },

    async processDeliveredNotifications() {
        if (!isHarmony() && Capacitor.isNativePlatform()) {
            try {
                // 获取那些推送到手机上，但用户没点，直接点桌面APP图标进来的通知
                const { notifications } = await PushNotifications.getDeliveredNotifications();
                if (notifications && notifications.length > 0) {
                    // 过滤出带我们业务链接的通知，取最新的一条
                    const targetNotif = notifications.reverse().find(n => n.data && n.data.url);
                    if (targetNotif && window.__ON_PUSH_CLICKED__) {
                        console.log('检测到系统托盘有未读推送，主动处理: ', targetNotif);
                        window.__ON_PUSH_CLICKED__(targetNotif.data);
                    }
                }
            } catch (error) {
                console.warn("[NativeBridge] 读取未读通知失败:", error);
            }
        }
    },

    // 获取推送 Token（精简版，只管注册和拿Token）
    async getPushToken() {
        if (isHarmony()) {
            const res = await window.HarmonyJSBridge.getPushToken();
            return { platform: 'harmony', ...JSON.parse(res) };
        } else if (isAndroid()) {
            const res = await Capacitor.nativePromise('PushBridge', 'getPushToken', {});
            return { platform: res.platform || 'android', success: res.success, token: res.token, message: res.msg };
        } else if (isIOS()) {
            return new Promise((resolve) => {
                // 注意：这里只保留获取Token相关的监听
                PushNotifications.addListener('registration', (token) => {
                    resolve({ platform: 'ios', success: true, token: token.value });
                });
                PushNotifications.addListener('registrationError', (err) => {
                    resolve({ platform: 'ios', success: false, message: JSON.stringify(err) });
                });
                PushNotifications.register();
            });
        }
        return { success: false, message: 'Unsupported web environment' };
    },
    async getPushPermissionStatus() {
        if (isHarmony()) {
            // 注意：鸿蒙端也需要提供一个纯净的只查不问接口，假设叫 checkPushPermissionStatus
            const res = JSON.parse(await window.HarmonyJSBridge.checkPushPermissionStatus());
            return {
                granted: res.granted,
                canPromptSettings: res.denied,
                isNotDetermined: !res.granted && !res.denied // 是否从未弹窗过
            };
        } else if (isAndroid()) {
            // Android 端同样需要纯净查询接口
            const res = await Capacitor.nativePromise('PushBridge', 'getPushPermissionStatus', {});
            return {
                granted: res.granted,
                canPromptSettings: res.denied,
                isNotDetermined: !res.granted && !res.denied
            };
        } else if (isIOS()) {
            const permStatus = await PushNotifications.checkPermissions();
            // receive 状态: 'prompt' (未决定/未弹窗), 'granted' (已授权), 'denied' (已拒绝)
            return {
                granted: permStatus.receive === 'granted',
                canPromptSettings: permStatus.receive === 'denied',
                isNotDetermined: permStatus.receive === 'prompt'
            };
        }
        return { granted: false, canPromptSettings: false, isNotDetermined: false };
    },

    // ==========================================
    // 动作 2：【主动请求】调起系统级权限授权弹窗
    // ==========================================
    async requestPushPermission() {
        if (isHarmony()) {
            const res = JSON.parse(await window.HarmonyJSBridge.requestPushPermission());
            return res.success;
        } else if (isAndroid()) {
            const res = await Capacitor.nativePromise('PushBridge', 'requestPushPermission', {});
            return res.success;
        } else if (isIOS()) {
            // 这行代码才会真正触发 iOS 的一次性系统弹窗！
            const permStatus = await PushNotifications.requestPermissions();
            return permStatus.receive === 'granted';
        }
        return false;
    },
    // 跳转系统设置
    async openAppSettings() {
        if (isHarmony()) {
            await window.HarmonyJSBridge.openAppSettings();
        } else if (isAndroid()) {
            await Capacitor.nativePromise('PushBridge', 'openAppSettings', {});
        } else if (isIOS()) {
            await Capacitor.nativePromise('SuperLink', 'openAppSettings', {});
        }
    },

    async doAppleLogin(options) {
        if (isHarmony()) {
            console.warn("HarmonyOS does not support native Apple Sign-In");
            return Promise.reject(new Error("unsupported_platform"));
        } else if (isIOS())  {
            const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
            return await SignInWithApple.authorize(options);
        }
    },

    async doWechatLogin() {
        if (isHarmony()) {
            const resString = await window.HarmonyJSBridge.doWechatLogin();
            return JSON.parse(resString);
        } else if (isAndroid()){
            return await Capacitor.nativePromise('WechatBridge', 'doWechatLogin', {});
        } else if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'doWechatLogin', {});
        }
    },

    async checkWechatInstalled() {
        try {
            if (isHarmony()) {
                // 如果鸿蒙端实现了对应接口，则调用；若没实现则返回 false
                if (window.HarmonyJSBridge && window.HarmonyJSBridge.checkWechatInstalled) {
                    const resString = await window.HarmonyJSBridge.checkWechatInstalled();
                    return JSON.parse(resString);
                }
                return { installed: false };
            } else if (isAndroid()) {
                return {"installed": true};
                // return await Capacitor.nativePromise('WechatBridge', 'checkWechatInstalled', {});
            } else if (isIOS()) {
                // iOS 端调用我们在 SuperLink 中注册的方法
                return await Capacitor.nativePromise('SuperLink', 'checkWechatInstalled', {});
            }
        } catch (error) {
            console.warn("[NativeBridge] 检测微信安装状态失败，默认降级为未安装", error);
        }
        // Web 环境或其他异常情况，默认返回未安装
        return { installed: false };
    },

    async pullUpAuthUI(options = { scene: 'login' }) {
        if (isHarmony()) {
            const resString = await window.HarmonyJSBridge.pullUpAuthUI(JSON.stringify(options));
            return JSON.parse(resString);
        } else if (isAndroid()) {
            return await Capacitor.nativePromise('AuthBridge', 'pullUpAuthUI', options);
        } else if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'pullUpAuthUI', options);
        }
    },

    async initAliyunSDK() {
        if (isHarmony()) {
            const resString = await window.HarmonyJSBridge.initAliyunSDK();
            console.log("initAliyunSDK 鸿蒙", resString);
            return JSON.parse(resString);
        } else if (isAndroid()) {
            console.log("initAliyunSDK Android");
            return await Capacitor.nativePromise('AuthBridge', 'initAliyunSDK', {});
        } else if (isIOS()) {
            console.log("initAliyunSDK IOS");
            return await Capacitor.nativePromise('SuperLink', 'initAliyunSDK', {});
        }
    },

    async checkEnv() {
        if (isHarmony()) {
            const resString = await window.HarmonyJSBridge.checkEnv();
            return JSON.parse(resString);
        } else if (isAndroid()) {
            return await Capacitor.nativePromise('AuthBridge', 'checkEnv', {});
        } else if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'checkEnv', {});
        }
    },

    async clearBadge() {
        try {
            if (isHarmony()) {
                if (window.HarmonyJSBridge && window.HarmonyJSBridge.clearBadge) {
                    await window.HarmonyJSBridge.clearBadge();
                }
            } else {
                await PushNotifications.removeAllDeliveredNotifications();
            }
        } catch (error) {
            console.warn("[NativeBridge] 清空角标失败:", error);
        }
    },

    async getAppId() {
        if (isHarmony()) {
            const res = await window.HarmonyJSBridge.getAppInfo();
            return JSON.parse(res).bundleName; // 确保鸿蒙侧提供 bundleName
        } else if (Capacitor.isNativePlatform()) {
            const info = await CapacitorApp.getInfo();
            return info.id; // 返回形如 "com.yourcompany.appa"
        }
        return "com.tls.aiagent.tally";
    },

    /**
     * 分享微信小程序卡片
     * @param {Object} options
     * @param {string} options.userName - 小程序原始ID (gh_xxxx)
     * @param {string} options.path - 小程序打开路径 (如: pages/invite/index?code=123)
     * @param {string} options.title - 卡片标题
     * @param {string} options.description - 卡片说明
     * @param {string} options.webpageUrl - 兜底 H5 链接
     * @param {string} [options.hdImageUrl] - 卡片封面图 URL (可选，5:4 比例)
     * @param {number} [options.miniProgramType] - 0:正式版, 1:开发版, 2:体验版
     */
    async shareMiniProgram(options) {
        if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'shareMiniProgram', options);
        } else if (isAndroid()) {
            return await Capacitor.nativePromise('WechatBridge', 'shareMiniProgram', options);
        }
        throw new Error("not_in_native_app");
    },

    /**
     * 保存图片到相册
     * @param {string} base64Data
     */
    async saveImageToAlbum(base64Data) {
        if (!base64Data) throw new Error("缺少图片数据");

        if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'saveImageToAlbum', { image: base64Data });
        } else if (isAndroid()) {
            // Android 端后续可在对应的 Bridge (如 WechatBridge 或专用媒体桥) 中实现
            return await Capacitor.nativePromise('MediaBridge', 'saveImageToAlbum', { image: base64Data });
        } else if (isHarmony()) {
            if (window.HarmonyJSBridge && window.HarmonyJSBridge.saveImageToAlbum) {
                const res = await window.HarmonyJSBridge.saveImageToAlbum(base64Data);
                return JSON.parse(res);
            }
        }

        // Web / 浏览器端兜底降级处理
        try {
            const link = document.createElement('a');
            link.href = base64Data;
            link.download = `账单明细_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return { success: true, isWeb: true };
        } catch (e) {
            throw new Error("当前环境不支持自动保存，请长按图片保存");
        }
    },

    /**
     * 微信直接分享图片 (拉起好友列表或朋友圈)
     * @param {Object} options
     * @param {string} options.image - Base64 字符串
     * @param {'session' | 'timeline' | number} [options.scene='session'] - session: 0 (好友), timeline: 1 (朋友圈)
     */
    async shareImageToWechat(options = {}) {
        if (!options.image) throw new Error("缺少待分享图片数据");

        const sceneCode = options.scene === 'timeline' || options.scene === 1 ? 1 : 0;
        const payload = {
            image: options.image,
            scene: sceneCode
        };

        if (isIOS()) {
            return await Capacitor.nativePromise('SuperLink', 'shareImageToWechat', payload);
        } else if (isAndroid()) {
            return await Capacitor.nativePromise('WechatBridge', 'shareImageToWechat', payload);
        }

        throw new Error("not_in_native_app");
    }
};